import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    ArrowRight,
    Search as SearchIcon,
    CircleSlash,
    Scale,
    Eye,
    ClipboardList,
    Globe,
    Siren,
    Shield,
    AlertTriangle,
    Trophy,
    Megaphone,
    Unlock,
    Users,
    Home,
    Briefcase,
    Gavel,
    Brain,
    Accessibility,
    BookOpen,
    Flag,
} from "lucide-react";
import { getMeta } from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import NewsletterSignup from "@/components/NewsletterSignup";
import LiveCounter from "@/components/LiveCounter";
import FeaturedBlogHero from "@/components/FeaturedBlogHero";

const ICON_MAP = {
    Search: SearchIcon,
    CircleSlash,
    Scale,
    Eye,
    ClipboardList,
    Globe,
    Siren,
    Shield,
    AlertTriangle,
    Trophy,
    Fist: Megaphone,
    Unlock,
    Users,
    Home,
    Briefcase,
    Gavel,
    Brain,
    Accessibility,
    BookOpen,
    Flag,
};

const Section = ({ children, className = "", id }) => (
    <section
        id={id}
        className={`border-t-2 border-white/20 ${className}`}
        data-testid={id ? `section-${id}` : undefined}
    >
        <div className="max-w-[1600px] mx-auto px-6 sm:px-10 py-20 sm:py-28">{children}</div>
    </section>
);

const Eyebrow = ({ children, num }) => (
    <div
        className="flex items-center gap-3 mb-6 font-mono text-[11px] uppercase tracking-[0.3em] text-white/60"
        data-testid="section-eyebrow"
    >
        {num && <span className="redact">{num}</span>}
        <span>{children}</span>
    </div>
);

export default function Landing() {
    const [meta, setMeta] = useState(null);

    useEffect(() => {
        getMeta()
            .then(setMeta)
            .catch(() => setMeta({}));
    }, []);

    return (
        <div className="bg-[#0a0a0a] text-white min-h-screen">
            <Header />

            {/* HERO */}
            <section
                className="relative overflow-hidden border-b-2 border-white/20"
                data-testid="hero-section"
            >
                <div
                    className="absolute inset-0 opacity-30 bg-cover bg-center"
                    style={{
                        backgroundImage:
                            "url(https://static.prod-images.emergentagent.com/jobs/3534535e-c0ee-417b-8717-e54206eac13a/images/d559e58f461e33ba01299a2c84226fb615ce61e4a7fc4eb12e2a47882df6e3bf.png)",
                    }}
                />
                <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a]/40 via-[#0a0a0a]/70 to-[#0a0a0a]" />
                <div className="absolute inset-0 hpm-grid-bg opacity-40" />

                <div className="relative max-w-[1600px] mx-auto px-6 sm:px-10 pt-20 pb-24 sm:pt-32 sm:pb-32">
                    <div className="flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.3em] text-white/70 mb-8 fade-up">
                        <span className="w-2 h-2 bg-[#FF3B30] inline-block animate-pulse" />
                        <span>LIVE / 10 BOTS ONLINE / 7 LANGUAGES / FREE FOREVER</span>
                    </div>

                    <h1
                        className="font-anton uppercase leading-[0.85] tracking-tight text-5xl sm:text-7xl md:text-8xl lg:text-9xl fade-up"
                        style={{ animationDelay: "0.1s" }}
                        data-testid="hero-headline"
                    >
                        THE SYSTEM<br />
                        IS{" "}
                        <span className="text-[#FF3B30]">RIGGED</span>.<br />
                        <span className="redact">WE LEVEL IT.</span>
                    </h1>

                    <p
                        className="mt-8 max-w-2xl font-sans text-base sm:text-lg text-white/80 leading-relaxed fade-up"
                        style={{ animationDelay: "0.2s" }}
                    >
                        The only free, full-spectrum, multilingual legal AI on Earth.{" "}
                        <span className="text-white font-semibold">
                            10 specialized AI bots
                        </span>{" "}
                        analyse your case in parallel. Built for people, not firms.
                        Because justice doesn't only speak English.
                    </p>

                    <div
                        className="mt-10 flex flex-wrap gap-4 fade-up"
                        style={{ animationDelay: "0.3s" }}
                    >
                        <Link
                            to="/hpm-submit"
                            className="btn-brutal btn-brutal-red"
                            data-testid="hero-cta-submit"
                        >
                            SUBMIT YOUR CASE <ArrowRight size={16} />
                        </Link>
                        <Link
                            to="/hpm-search"
                            className="btn-brutal"
                            data-testid="hero-cta-search"
                        >
                            SEARCH 6.5M+ CASES
                        </Link>
                    </div>

                    <div
                        className="mt-16 grid grid-cols-2 sm:grid-cols-4 border-2 border-white/20 fade-up"
                        style={{ animationDelay: "0.4s" }}
                        data-testid="hero-stats"
                    >
                        {(meta?.core_problems || []).slice(0, 4).map((p, i) => (
                            <div
                                key={i}
                                className="p-5 sm:p-8 border-white/20 [&:not(:last-child)]:border-r-2 [&:nth-child(3)]:border-l-0 sm:[&:nth-child(3)]:border-l-2 [&:nth-child(1)]:border-b-2 [&:nth-child(2)]:border-b-2 sm:[&:nth-child(1)]:border-b-0 sm:[&:nth-child(2)]:border-b-0"
                            >
                                <div className="font-anton text-4xl sm:text-5xl text-[#FF3B30] leading-none mb-2">
                                    {p.stat}
                                </div>
                                <div className="font-sans text-[11px] sm:text-xs text-white/70 leading-snug">
                                    {p.desc}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* MARQUEE */}
            <div className="border-b-2 border-white/20 py-8 marquee bg-[#0a0a0a]">
                <div className="marquee-track font-anton text-6xl sm:text-8xl uppercase leading-none">
                    {Array(8)
                        .fill(0)
                        .map((_, i) => (
                            <span key={i} className="mx-12">
                                {i % 2 === 0 ? (
                                    <span className="text-stroke">JUSTICE IS A RIGHT</span>
                                ) : (
                                    <span className="text-[#FF3B30]">NOT A PRIVILEGE //</span>
                                )}
                            </span>
                        ))}
                </div>
            </div>

            <LiveCounter />

            <FeaturedBlogHero />

            {/* 10 BOTS */}
            <Section id="bots">
                <Eyebrow num="01">THE TEN-BOT ARSENAL</Eyebrow>
                <h2 className="font-anton text-4xl sm:text-6xl uppercase leading-none mb-4">
                    Ten Specialists.<br />
                    <span className="text-[#FF3B30]">One Mission.</span>
                </h2>
                <p className="max-w-2xl font-sans text-white/70 mb-12">
                    Every bot runs in parallel. Each one a domain expert. No general chatbot. No generic answer.
                </p>

                <div
                    className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 border-2 border-white/20"
                    data-testid="bots-grid"
                >
                    {(meta?.bots || []).map((b, i) => {
                        const Icon = ICON_MAP[b.icon] || SearchIcon;
                        const isAccent = b.key === "violation_identifier";
                        return (
                            <div
                                key={b.key}
                                data-testid={`bot-card-${b.key}`}
                                className={`p-6 lg:p-8 border-white/20 lg:[&:not(:nth-child(5n))]:border-r-2 [&:not(:last-child)]:border-b-2 lg:[&:nth-child(-n+5)]:border-b-2 lg:[&:nth-child(n+6)]:border-b-0 transition-colors hover:bg-white/5 ${
                                    isAccent ? "bg-[#FF3B30] text-white hover:bg-[#FF3B30]" : ""
                                }`}
                            >
                                <div
                                    className={`font-mono text-[10px] uppercase tracking-[0.25em] mb-4 ${
                                        isAccent ? "text-white" : "text-white/50"
                                    }`}
                                >
                                    {b.code}
                                </div>
                                <Icon
                                    size={28}
                                    strokeWidth={1.5}
                                    className={isAccent ? "text-white" : "text-white"}
                                />
                                <div className="font-anton text-2xl uppercase mt-4 leading-none">
                                    {b.name}
                                </div>
                                <div
                                    className={`mt-3 text-sm leading-snug ${
                                        isAccent ? "text-white/90" : "text-white/70"
                                    }`}
                                >
                                    {b.mission}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </Section>

            {/* HOW IT WORKS */}
            <Section id="how">
                <Eyebrow num="02">HOW IT WORKS</Eyebrow>
                <h2 className="font-anton text-4xl sm:text-6xl uppercase leading-none mb-12">
                    Three steps.<br />Your power back.
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 border-2 border-white/20">
                    {(meta?.how_it_works || []).map((s, i) => (
                        <div
                            key={s.step}
                            className="p-8 border-white/20 md:[&:not(:last-child)]:border-r-2 [&:not(:last-child)]:border-b-2 md:[&:not(:last-child)]:border-b-0"
                            data-testid={`how-step-${s.step}`}
                        >
                            <div className="font-anton text-7xl text-white/15 leading-none">{s.step}</div>
                            <div className="font-anton text-2xl uppercase mt-4">{s.title}</div>
                            <div className="font-sans text-white/70 mt-3 text-sm leading-relaxed">{s.copy}</div>
                        </div>
                    ))}
                </div>
            </Section>

            {/* WHO WE SERVE */}
            <Section id="serve">
                <Eyebrow num="03">WHO WE SERVE</Eyebrow>
                <h2 className="font-anton text-4xl sm:text-6xl uppercase leading-none mb-12">
                    Built for the people<br />the system left behind.
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 border-2 border-white/20">
                    {(meta?.who_we_serve || []).map((w, i) => {
                        const Icon = ICON_MAP[w.icon] || Megaphone;
                        return (
                            <div
                                key={i}
                                className="p-6 border-white/20 lg:[&:not(:nth-child(4n))]:border-r-2 sm:[&:not(:nth-child(3n))]:border-r-2 lg:[&:nth-child(-n+8)]:border-b-2 sm:[&:nth-child(-n+9)]:border-b-2 [&:nth-child(odd)]:border-r-2"
                                data-testid={`serve-${i}`}
                            >
                                <Icon size={24} strokeWidth={1.5} className="text-[#FF3B30]" />
                                <div className="font-mono text-sm uppercase tracking-wider mt-4 leading-snug">
                                    {w.label}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </Section>

            {/* SYSTEM FAILURES */}
            <Section id="failures">
                <Eyebrow num="04">THE 8 SYSTEM FAILURES</Eyebrow>
                <h2 className="font-anton text-4xl sm:text-6xl uppercase leading-none mb-4">
                    What we're up against.
                </h2>
                <p className="max-w-2xl font-sans text-white/70 mb-12">
                    These aren't opinions. They are documented, sourced, undisputed.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 border-2 border-white/20">
                    {(meta?.system_failures || []).map((f, i) => (
                        <div
                            key={f.num}
                            className="p-8 border-white/20 md:[&:nth-child(odd)]:border-r-2 [&:not(:last-child)]:border-b-2"
                            data-testid={`failure-${f.num}`}
                        >
                            <div className="flex items-baseline gap-4 mb-3">
                                <div className="font-anton text-3xl text-[#FF3B30]">{f.num}</div>
                                <div className="font-anton text-xl sm:text-2xl uppercase leading-tight">
                                    {f.title}
                                </div>
                            </div>
                            <div className="font-mono text-xs uppercase tracking-wider text-white/80 mb-2">
                                <span className="redact">{f.stat}</span>
                            </div>
                            <p className="font-sans text-sm text-white/70 leading-relaxed mt-3">
                                {f.desc}
                            </p>
                            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mt-4">
                                SOURCE — {f.source}
                            </p>
                        </div>
                    ))}
                </div>
            </Section>

            {/* COMPETITOR / ADVANTAGE */}
            <Section id="why">
                <Eyebrow num="05">WHY HPM</Eyebrow>
                <h2 className="font-anton text-4xl sm:text-6xl uppercase leading-none mb-12">
                    Nothing else<br />comes close.
                </h2>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 border-2 border-white/20">
                    <div className="p-8 border-white/20 border-b-2 lg:border-b-0 lg:border-r-2">
                        <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-white/50 mb-6">
                            COMPETITORS
                        </div>
                        <div className="space-y-5">
                            {(meta?.competitors || []).map((c, i) => (
                                <div key={i} className="border-l-2 border-white/30 pl-4">
                                    <div className="flex items-baseline justify-between">
                                        <div className="font-anton text-2xl uppercase">{c.name}</div>
                                        <div className="font-mono text-xs text-white/60">{c.price}</div>
                                    </div>
                                    <div className="font-sans text-sm text-white/70 mt-1">{c.focus}</div>
                                    <div className="font-mono text-xs text-[#FF3B30] mt-2 leading-snug">
                                        × {c.weakness}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="p-8 bg-[#FF3B30] text-white">
                        <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-white/80 mb-6">
                            HPM ADVANTAGES
                        </div>
                        <ul className="space-y-3 font-sans text-sm">
                            {(meta?.advantages || []).map((a, i) => (
                                <li key={i} className="flex gap-3">
                                    <span className="font-mono text-xs mt-1">✓</span>
                                    <span>{a}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </Section>

            {/* FINAL CTA */}
            <Section id="cta" className="border-b-2">
                <div className="text-center py-12">
                    <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-white/50 mb-6">
                        06 / FREE FOREVER / NO CARD / NO ACCOUNT
                    </div>
                    <h2 className="font-anton text-5xl sm:text-7xl md:text-9xl uppercase leading-[0.85]">
                        TAKE YOUR<br />
                        <span className="text-[#FF3B30]">POWER</span> BACK.
                    </h2>
                    <div className="mt-12 flex flex-wrap justify-center gap-4">
                        <Link
                            to="/hpm-submit"
                            className="btn-brutal btn-brutal-red"
                            data-testid="footer-cta-submit"
                        >
                            START YOUR CASE <ArrowRight size={16} />
                        </Link>
                        <Link to="/hpm-search" className="btn-brutal">
                            SEARCH PRECEDENTS
                        </Link>
                    </div>
                </div>
            </Section>

            {/* NEWSLETTER */}
            <Section bg="bg-[#0a0a0a]" border>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                    <div>
                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FFCC00] mb-4">
                            FREE WEEKLY BRIEF · 7 LANGUAGES · NO SPAM
                        </div>
                        <h2
                            className="font-anton uppercase leading-[0.9] text-5xl sm:text-6xl"
                            data-testid="newsletter-headline"
                        >
                            One email.<br />
                            One <span className="text-[#FFCC00]">question worth asking</span>.<br />
                            Every Sunday.
                        </h2>
                        <p className="mt-6 font-sans text-white/70 text-base sm:text-lg leading-relaxed max-w-lg">
                            We curate the week's most important legal questions, cite the statutes,
                            list the free resources, and add state-specific tools. You decide what
                            to do. We just make sure you know what to think about.
                        </p>
                        <ul className="mt-6 space-y-2 font-mono text-[11px] uppercase tracking-[0.25em] text-white/60">
                            <li>↳ Plain English. Statutes cited. Free resources.</li>
                            <li>↳ In English, Español, 中文, हिन्दी, العربية, Français, Português.</li>
                            <li>↳ State-specific tools for your jurisdiction.</li>
                            <li>↳ Forward to one person who needs it. That's how we level it.</li>
                        </ul>
                    </div>
                    <NewsletterSignup source="landing" accent="#FFCC00" />
                </div>
            </Section>

            <Footer disclaimer={meta?.disclaimer || ""} />
        </div>
    );
}
